import React, { useState, useRef } from "react";

const MAX_BYTES = 512 * 1024; // 512 KB (mirrors server)

// CI/CD setup card for the hub's `deployment_setup_pending` step (FRONTEND_DEPLOYMENT_SETUP.md).
// data: { technology, stage, message, options[], deployTarget, default, canSkip, skipHint,
//         uploadEndpoint, note }
// onSubmit(payload) → parent sends the next hub request. payload is either
//   { deploymentSetup: { ciPlatform } } | { deploymentSetup: { ownPipeline: true } } | { prompt: "..." }
export default function DeploymentSetupCard({ data, sessionId, backendUrl, onSubmit }) {
  const d = data || {};
  const stage = d.stage || null;
  const options = Array.isArray(d.options) ? d.options : [];
  const defaultCi = d.default && d.default.ciPlatform;
  const isMule = d.technology === "mule";

  const [showUpload, setShowUpload] = useState(stage === "awaiting_upload");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [detected, setDetected] = useState(null);
  const inputRef = useRef();

  const uploadEndpoint = d.uploadEndpoint || "/api/v1/hub/upload-pipeline";

  const pickFile = (f) => {
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["yml", "yaml"].includes(ext)) { setUploadErr("Only .yml or .yaml files are accepted."); return; }
    if (f.size > MAX_BYTES) { setUploadErr("Pipeline YAML exceeds the 512 KB limit."); return; }
    if (f.size === 0) { setUploadErr("The selected file is empty."); return; }
    setFile(f); setUploadErr(""); setWarnings([]); setDetected(null);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) pickFile(dropped);
  };

  // Upload the YAML FIRST, then answer with ownPipeline: true (spec §4 — order matters).
  const handleUploadThenOwn = async () => {
    if (!file || uploading) return;
    setUploading(true); setUploadErr(""); setWarnings([]);
    try {
      const form = new FormData();
      form.append("file", file);           // field name MUST be "file"
      form.append("sessionId", sessionId);
      const res = await fetch(backendUrl + uploadEndpoint, {
        method: "POST",
        headers: { "x-access-token": localStorage.getItem("token") || "" }, // browser sets the boundary
        body: form,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || json.message || "Upload failed");
      setDetected(json.detectedPlatform || null);
      setWarnings(Array.isArray(json.warnings) ? json.warnings : []);
      // Hand off to the parent — the backend now decides (platform detected → continue;
      // null → it re-asks with stage "awaiting_type").
      onSubmit({ deploymentSetup: { ownPipeline: true } });
    } catch (err) {
      setUploadErr(err.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const chooseCi = (value) => onSubmit({ deploymentSetup: { ciPlatform: value } });
  const skip = () => onSubmit({ prompt: "skip" });

  const target = d.deployTarget && (d.deployTarget.label || d.deployTarget.value);

  const dropzone = (
    <div>
      <div
        className={`sh-existing-api-dropzone${dragOver ? " drag-over" : ""}`}
        onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {file
          ? <span>📄 {file.name} <span style={{ opacity: 0.6, fontSize: 11 }}>({(file.size / 1024).toFixed(1)} KB)</span></span>
          : <span>Drop your pipeline <strong>.yml</strong> here, or click to browse <span style={{ opacity: 0.6, fontSize: 11 }}>(.yml / .yaml, ≤ 512 KB)</span></span>}
      </div>
      <input ref={inputRef} type="file" accept=".yml,.yaml" style={{ display: "none" }}
        onChange={(e) => { if (e.target.files[0]) pickFile(e.target.files[0]); }} />
      {uploadErr && <div className="sh-existing-api-error">{uploadErr}</div>}
      {detected && <div style={{ fontSize: 12, color: "#2EBFA5", marginTop: 8 }}>✓ Detected: {detected}</div>}
      {warnings.map((w, i) => <div key={i} style={{ color: "#f0b429", fontSize: 12, marginTop: 4 }}>⚠ {w}</div>)}
      <div className="sh-existing-api-actions">
        <button className="sh-existing-api-btn-primary" disabled={!file || uploading} onClick={handleUploadThenOwn}>
          {uploading ? "Uploading…" : "Upload & use this pipeline"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="sh-existing-api-card">
      {d.note && (
        <div style={{ fontSize: 12, color: "#f0b429", padding: "4px 2px 8px", lineHeight: 1.5 }}>ⓘ {d.note}</div>
      )}

      {/* Stage: the file didn't declare its CI system → ask which one it is for (send prompt text). */}
      {stage === "awaiting_type" ? (
        <div>
          <div style={{ fontSize: 13, color: "#c9d6ea", padding: "2px 2px 10px", lineHeight: 1.6 }}>
            {d.message || "Is your pipeline file for Azure DevOps or GitHub Actions?"}
          </div>
          <div className="sh-existing-api-actions" style={{ display: "flex", gap: 8 }}>
            <button className="sh-existing-api-btn-primary" onClick={() => onSubmit({ prompt: "azure" })}>Azure DevOps</button>
            <button className="sh-existing-api-btn-primary" onClick={() => onSubmit({ prompt: "github" })}>GitHub Actions</button>
          </div>
        </div>
      ) : (
        <div>
          {target && (
            <div style={{ fontSize: 12, color: "#7a93bc", padding: "2px 2px 10px", lineHeight: 1.6 }}>
              Deploys to <strong style={{ color: "#c9d6ea" }}>{target}</strong>
            </div>
          )}

          {/* awaiting_upload shows the picker first; the question stage shows choices, and the
              Mule "own-pipeline" option reveals the picker on click. */}
          {showUpload ? (
            <div>
              {dropzone}
              <div style={{ fontSize: 11, color: "#7a93bc", margin: "10px 0 4px" }}>…or use a built-in pipeline:</div>
              <div className="sh-existing-api-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="sh-existing-api-tab" onClick={() => chooseCi("azure-devops")}>Built-in Azure DevOps</button>
                <button className="sh-existing-api-tab" onClick={() => chooseCi("github-actions")}>Built-in GitHub Actions</button>
                {stage !== "awaiting_upload" && (
                  <button className="sh-existing-api-tab" onClick={() => setShowUpload(false)}>↩ back</button>
                )}
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {options.map((o) => {
                if (o.kind === "upload") {
                  return (
                    <button key={o.value} className="sh-existing-api-tab" style={{ textAlign: "left" }}
                      onClick={() => setShowUpload(true)}>
                      📤 {o.label || "Upload your own pipeline YAML"}
                    </button>
                  );
                }
                const disabled = o.available === false;
                return (
                  <button key={o.value} className="sh-existing-api-tab" disabled={disabled}
                    style={{ textAlign: "left", opacity: disabled ? 0.5 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
                    onClick={() => !disabled && chooseCi(o.value)}>
                    {o.label || o.value}
                    {!disabled && o.value === defaultCi && <span style={{ marginLeft: 8, fontSize: 10, color: "#2EBFA5" }}>• default</span>}
                    {disabled && <span style={{ marginLeft: 8, fontSize: 10, color: "#7a93bc" }}>Coming soon</span>}
                    {!disabled && isMule && o.kind === "ci" && <span style={{ marginLeft: 8, fontSize: 10, color: "#7a93bc" }}>built-in pipeline</span>}
                  </button>
                );
              })}
            </div>
          )}

          {d.canSkip && (
            <div style={{ marginTop: 10, textAlign: "center" }}>
              <button className="sh-existing-api-skip" onClick={skip}>
                Skip{d.skipHint ? ` — ${d.skipHint}` : ""}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
